import React, { useState, useEffect, useRef } from 'react';


// import InstallReviewDialog from './InstallReviewDialog';
import InstallConfirmationDialog from './InstallConfirmationDialog';
import RequestInstallDialog from './RequestInstallDialog';
import InstallStatusDrawer from './InstallStatusDrawer';
import { useNavigate } from 'react-router-dom';
import { Box } from '@ellucian/react-design-system/core';
import { Typography } from '@ellucian/react-design-system/core';
import { Alert, Button, StatusLabel, Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton } from '@ellucian/react-design-system/core';
import RequestInfoDialog from './RequestInfoDialog';
import { Icon, CheckFeedback } from '@ellucian/ds-icons/lib';
import Award from '@ellucian/ds-icons/lib/Award';
import BadgeDollar from '@ellucian/ds-icons/lib/BadgeDollar';
import Employment from '@ellucian/ds-icons/lib/Employment';
import HandsHelping from '@ellucian/ds-icons/lib/HandsHelping';
import { sanitizeContent, createPrefilledForm, calculateSelfEntitlementCounts } from '../../utils/helpers';
import { trackSolutionInstallRequest, trackUserLogin } from '../../utils/analytics';
import SelfEntitleButton from '../../components/SelfEntitleButton';
import { useSelfEntitlement } from '../../hooks/useSelfEntitlement';

// Alert variant constant (aligned with Ellucian design system)
const PAGE_VARIANT = 'page';

type ManifestLookupKey = 'banner' | 'colleague';

const hasInstallableManifest = (manifestName: unknown): boolean => {
    if (typeof manifestName !== 'string' || manifestName.trim().length === 0) {
        return false;
    }

    try {
        const parsedManifest = JSON.parse(manifestName) as Partial<Record<ManifestLookupKey, unknown>>;
        return ['banner', 'colleague'].some((manifestLookupKey) => {
            const manifestValue = parsedManifest[manifestLookupKey as ManifestLookupKey];

            return typeof manifestValue === 'string' && manifestValue.trim().length > 0;
        });
    } catch {
        return false;
    }
};

interface SolutionHeaderProps {
    logo: string;
    title: string;
    headline?: string;
    rating: string;
    installs: number;
    description: string;
    installing: boolean;
    setInstalling: (installing: boolean) => void;
    installComplete: boolean;
    setInstallComplete: (complete: boolean) => void;
    isEntitled?: boolean;
    reviewButtonLabel?: string;
    isMobile?: boolean;
    isTablet?: boolean;
    isLoggedIn?: boolean;
    solution: any;
}

const SolutionHeader: React.FC<SolutionHeaderProps> = (props: SolutionHeaderProps) => {
    const {
        logo,
        title,
        headline,
        rating,
        installs,
        description,
        installing,
        setInstalling,
        installComplete,
        setInstallComplete,
        isEntitled = false,
        // reviewButtonLabel = 'LEAVE A REVIEW',
        isMobile = false,
        isTablet = false,
        isLoggedIn = false,
        solution,
    } = props;
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTenants, setSelectedTenants] = useState<string>(''); // Changed from array to single string
    const [tenants, setTenants] = useState<any[]>([]);
    // const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [requestInstallDialogOpen, setRequestInstallDialogOpen] = useState(false); // New state for RequestInstallDialog
    const [installStatusOpen, setInstallStatusOpen] = useState(false);
    const isBetaUser = localStorage.getItem('isBetaUser') === 'true';
    // State for Request Information dialog
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [infoForm, setInfoForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        institution: '',
        jobLevel: '',
        functionalRole: '',
    });

    const isMarketoSolution = ['Ellucian Premium', 'Partner Resell'].includes(
        solution?.productizedIntegrationType ?? ''
    );

    // Ref to hold the preloaded Marketo form instance.
    const mktoFormRef = useRef<any>(null);
    const MKTO_BASE_URL = '//lp.ellucian.com';
    const MKTO_SDK_SRC = `${MKTO_BASE_URL}/js/forms2/js/forms2.min.js`;

    // Pre-load Marketo form once on mount. Register onSuccess to prevent redirect.
    useEffect(() => {
        if (!isMarketoSolution) return;

        const loadForm = () => {
            const mkto = (window as any).MktoForms2;
            if (!mkto || mktoFormRef.current) return;
            mkto.loadForm(MKTO_BASE_URL, '085-MHT-312', 2266, (form: any) => {
                // Prevent Marketo's default redirect on success
                form.onSuccess(() => false);
                // Force valid on every validate call — registered once at preload
                form.onValidate(() => { form.submittable(true); });
                mktoFormRef.current = form;
            });
        };

        if ((window as any).MktoForms2) {
            loadForm();
        } else if (document.querySelector(`script[src="${MKTO_SDK_SRC}"]`)) {
            const poll = setInterval(() => {
                if ((window as any).MktoForms2) { clearInterval(poll); loadForm(); }
            }, 100);
        } else {
            const script = document.createElement('script');
            script.src = MKTO_SDK_SRC;
            script.onload = loadForm;
            document.head.appendChild(script);
        }
    }, [isMarketoSolution]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleMarketoSubmit = (form: typeof infoForm): Promise<void> => {
        return new Promise((resolve, reject) => {
            const mktoForm = mktoFormRef.current;
            if (!mktoForm) {
                reject(new Error('Form not ready. Please try again.'));
                return;
            }

            // Map our UI Job Level values → Marketo Function__c picklist ("Title:" field)
            const jobLevelToFunction: Record<string, string> = {
                'Executive': 'Vice President',
                'Manager': 'Manager',
                'Staff': 'Other',
                'Faculty': 'Faculty',
                'Other': 'Other',
            };

            // Map our UI Functional Role values → Marketo Department__c picklist ("Function:" field)
            const roleToDoepartment: Record<string, string> = {
                'IT': 'IT',
                'Finance': 'Finance',
                'Academic Affairs': 'School/Program',
                'Student Services': 'Advising/Student Affairs',
                'Other': 'Other',
            };

            // State__c is required by Marketo when Country__c is US or Canada (picklist — must be a valid value)
            const stateForCountry: Record<string, string> = {
                'United States': 'VA',  // default US state (Virginia — Ellucian HQ)
                'Canada': 'ON',  // default Canadian province
            };

            const fieldValues: Record<string, string> = {
                FirstName: form.firstName,
                LastName: form.lastName,
                Email: form.email,
                Company: form.institution,
                Country__c: form.country,
                // State__c becomes required when Country is US or Canada — pass a default
                State__c: stateForCountry[form.country] ?? '',
                // Function__c = "Title" in Marketo → maps from jobLevel
                Function__c: jobLevelToFunction[form.jobLevel] ?? 'Other',
                // Department__c = "Function" in Marketo → maps from functionalRole
                Department__c: roleToDoepartment[form.functionalRole] ?? 'Other',
                mktoCommentCapture: `${props.title} | ${window.location.href}`,
            };

            mktoForm.vals(fieldValues);

            // Use Marketo's embedded jQuery to trigger field watchers
            const $formEl = mktoForm.getFormElem();
            Object.entries(fieldValues).forEach(([name, value]) => {
                $formEl.find(`[name="${name}"]`).val(value).trigger('change').trigger('blur');
            });

            // Reject on fallback so caller shows error instead of false success
            const fallback = setTimeout(() => {
                reject(new Error('Submission timed out. Please try again.'));
            }, 8000);

            mktoForm.onSuccess(() => {
                clearTimeout(fallback);
                resolve();
                return false; // prevent redirect
            });

            try {
                mktoForm.submit();
            } catch (e) {
                clearTimeout(fallback);
                reject(e);
            }
        });
    };

    const [selfEntitleAlertOpen, setSelfEntitleAlertOpen] = useState(false);
    const [selfEntitleAlertText, setSelfEntitleAlertText] = useState('');

    // Auto-dismiss alert after 30 seconds (made longer for visibility during testing)
    useEffect(() => {
        if (selfEntitleAlertOpen) {
            const timer = setTimeout(() => {
                setSelfEntitleAlertOpen(false);
            }, 30000);
            return () => clearTimeout(timer);
        }
    }, [selfEntitleAlertOpen]);

    // Get self-entitlement status for ERP type
    const { status } = useSelfEntitlement(isLoggedIn);
    const isInstallableForCurrentErp = hasInstallableManifest(solution?.manifestName);

    // Pre-fill form with user data when dialog opens
    useEffect(() => {
        if (infoDialogOpen && isLoggedIn) {
            const prefilledForm = createPrefilledForm();
            setInfoForm(prefilledForm);
        }
    }, [infoDialogOpen, isLoggedIn]);

    // Load tenants from localStorage
    useEffect(() => {
        const tenantsData = localStorage.getItem('tenantsData');
        if (tenantsData) {
            setTenants(JSON.parse(tenantsData));
        }
    }, []);

    const infoFormValid =
        infoForm.firstName.trim() &&
        infoForm.lastName.trim() &&
        infoForm.email.trim() &&
        infoForm.country.trim() &&
        infoForm.institution.trim() &&
        infoForm.jobLevel.trim() &&
        infoForm.functionalRole.trim();
    // Simulate install completion after 2 seconds
    // Remove effect that resets installing to false, so 'Install Requested' stays visible

    return (
        <>
            <Box
                sx={{
                    position: 'fixed',
                    top: { xs: '80px', md: '88px' },
                    left: 0,
                    right: 0,
                    zIndex: 1500,
                    px: { xs: 2, md: 3 },
                }}
            >
                <Alert
                    alertType="error"
                    id="selfEntitleErrorAlert"
                    onClose={() => setSelfEntitleAlertOpen(false)}
                    open={selfEntitleAlertOpen}
                    variant="inline"
                    sx={{ padding: '12px 16px', minHeight: 48, width: '100%' }}
                >
                    <Typography variant="body1" sx={{ fontSize: '18px', fontWeight: 400, lineHeight: '26px' }}>
                        {selfEntitleAlertText}
                    </Typography>
                </Alert>
            </Box>
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: isMobile ? '16px' : '24px 32px 32px 32px',
                    background: 'linear-gradient(90deg, #FFF 0%, #EAFDEC 100%)',
                    boxSizing: 'border-box',
                    zIndex: 1,
                }}
            >
                {/* Back Button */}
                <Box sx={{ ml: '-8px', height: 40, display: 'flex', alignItems: 'center' }}>
                    <Button
                        variant="text"
                        onClick={() => navigate(-1)}
                        startIcon={<Icon name="chevron-left" style={{ width: 14, height: 14, color: '#0056D2' }} />}
                        sx={{
                            display: 'flex',
                            padding: '4px 8px',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            borderRadius: '4px',
                            fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                            fontSize: 1,
                            fontStyle: 'normal',
                            fontWeight: 700,
                            lineHeight: '20px',
                            letterSpacing: '0.32px',
                            color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                            background: '#fff !important',
                            minWidth: 0,
                            boxShadow: 'none',
                            outline: 'none',
                            textTransform: 'none',
                            marginLeft: 0,
                            '&:hover': {
                                background: '#fff !important',
                                color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                                boxShadow: 'none',
                            },
                            '&:focus': {
                                outline: 'none',
                                boxShadow: 'none',
                                background: '#fff !important',
                                color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                            },
                            '&:active': {
                                outline: 'none',
                                boxShadow: 'none',
                                background: '#fff !important',
                                color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                            },
                        }}
                    >
                        Back
                    </Button>
                </Box>
                {/* Header Content */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: isMobile ? '16px' : (isTablet ? '24px' : '32px'),
                        alignSelf: 'stretch',
                        flexDirection: isMobile ? 'column' : 'row',
                        marginTop: '15px',
                        ...(isMobile && { alignItems: 'center' })
                    }}
                >
                    {/* Logo */}
                    <Box
                        sx={{
                            width: isMobile ? 86 : (isTablet ? 128 : 160),
                            height: isMobile ? 86 : (isTablet ? 128 : 160),
                            aspectRatio: '1/1',
                            borderRadius: '6px',
                            background: '#FFFFFF',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            border: '1px solid #E9E9E9',
                        }}
                    >
                        <img
                            src={logo}
                            alt="Solution Logo"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                objectPosition: 'center',
                                display: 'block',
                            }}
                        />
                    </Box>
                    {/* Right Content */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
                        {/* Title + Install Button */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                alignSelf: 'stretch',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: isMobile ? '16px' : '0',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'var(--Neutral-neutral-600, #151618)',
                                    fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                                    fontSize: isMobile ? '20px' : (isTablet ? '28px' : '32px'),
                                    fontStyle: 'normal',
                                    fontWeight: 700,
                                    lineHeight: isMobile ? '28px' : (isTablet ? '36px' : '48px'),
                                    letterSpacing: isMobile ? '0.6px' : (isTablet ? '0.84px' : '0.96px'),
                                    ...(isMobile && { textAlign: 'center' })
                                }}
                            >
                                {title}
                            </Typography>
                            {isLoggedIn ? (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    ...(isMobile && { width: '100%' })
                                }}>

                                    {/* Show    entitlement badge based on actual isEntitled status */}
                                    {isEntitled && (
                                        <StatusLabel
                                            id={installComplete ? "solutionHeader_Installed" : "solutionHeader_Entitled"}
                                            text={installComplete ? "INSTALLED" : "ENTITLED"}
                                            type="success"
                                            icon={installComplete ? <CheckFeedback /> : <Award />}
                                        />
                                    )}

                                    {/* Show Additional Purchase badge */}
                                    {isLoggedIn && solution?.productizedIntegrationType === 'Ellucian Premium' && (
                                        <StatusLabel
                                            id="solutionHeader_AdditionalPurchase"
                                            text="Additional Purchase"
                                            type="draft"
                                            icon={<BadgeDollar />}
                                        />
                                    )}
                                    {/* Review button removed as per request */}
                                    {isEntitled && (
                                        <Tooltip title="Install Status" >
                                            <IconButton color="primary"
                                                aria-label="Install Status"
                                                size="small"
                                                onClick={() => setInstallStatusOpen(true)}>
                                                <Icon name="server" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {!installComplete && (
                                        installing ? (
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                disableElevation
                                                disabled
                                                startIcon={<Icon name="check" style={{ width: 20, height: 20 }} />}
                                                sx={{
                                                    fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                                    color: 'var(--Neutral-neutral-100, #FFF)',
                                                    fontSize: '16px',
                                                    fontStyle: 'normal',
                                                    fontWeight: 600,
                                                    lineHeight: '110%', // 17.6px
                                                    textTransform: 'capitalize',
                                                }}
                                            >
                                                Install Started
                                            </Button>
                                        ) : solution.productizedIntegrationType === 'Partner Referral' && isBetaUser ? (
                                            /* Partner Referral: show both Install and Request Information regardless of entitlement.
                                               SKUs for referral integrations are never created in Salesforce, so entitlement is not required. */
                                            <Box sx={{ display: 'flex', gap: '8px', flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                                                {isInstallableForCurrentErp ? (
                                                    <Button
                                                        color="primary"
                                                        variant="contained"
                                                        disableElevation
                                                        onClick={() => {
                                                            trackSolutionInstallRequest(solution);
                                                            setRequestInstallDialogOpen(true);
                                                        }}
                                                        sx={{
                                                            fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                                            color: 'var(--Neutral-neutral-100, #FFF)',
                                                            fontSize: '16px',
                                                            fontStyle: 'normal',
                                                            fontWeight: 600,
                                                            lineHeight: '110%',
                                                            textTransform: 'capitalize',
                                                            width: isMobile ? '100%' : 'auto',
                                                        }}
                                                    >
                                                        Install
                                                    </Button>
                                                ) : (
                                                    <Tooltip title="See documentation for further instructions.">
                                                        <span style={{ display: 'inline-flex', width: isMobile ? '100%' : 'auto' }}>
                                                            <Button
                                                                color="primary"
                                                                variant="contained"
                                                                disableElevation
                                                                disabled
                                                                sx={{
                                                                    fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                                                    color: 'var(--Neutral-neutral-100, #FFF)',
                                                                    fontSize: '16px',
                                                                    fontStyle: 'normal',
                                                                    fontWeight: 600,
                                                                    lineHeight: '110%',
                                                                    textTransform: 'capitalize',
                                                                    width: isMobile ? '100%' : 'auto',
                                                                    '&.Mui-disabled': {
                                                                        color: 'var(--Neutral-neutral-400, #9CA3AF)',
                                                                        backgroundColor: 'var(--Neutral-neutral-200, #E5E7EB)',
                                                                    },
                                                                }}
                                                            >
                                                                Install
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    disableElevation
                                                    onClick={() => setInfoDialogOpen(true)}
                                                    sx={{
                                                        fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                                        color: 'var(--Neutral-neutral-100, #FFF)',
                                                        fontSize: '16px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 600,
                                                        lineHeight: '110%',
                                                        textTransform: 'capitalize',
                                                        width: isMobile ? '100%' : 'auto',
                                                    }}
                                                >
                                                    Request Information
                                                </Button>
                                            </Box>
                                        ) : isEntitled ? (
                                            isInstallableForCurrentErp ? (
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    disableElevation
                                                    onClick={() => {
                                                        trackSolutionInstallRequest(solution);
                                                        setRequestInstallDialogOpen(true);
                                                    }}
                                                    sx={{
                                                        fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                                        color: 'var(--Neutral-neutral-100, #FFF)',
                                                        fontSize: '16px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 600,
                                                        lineHeight: '110%', // 17.6px
                                                        textTransform: 'capitalize',
                                                    }}
                                                >
                                                    Install
                                                </Button>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: isMobile ? 'stretch' : 'flex-end',
                                                        gap: '8px',
                                                        width: isMobile ? '100%' : 'auto',
                                                    }}
                                                >
                                                    <Tooltip title="See documentation for further instructions.">
                                                        <span style={{ display: 'inline-flex', width: isMobile ? '100%' : 'auto' }}>
                                                            <Button
                                                                color="primary"
                                                                variant="contained"
                                                                disableElevation
                                                                disabled
                                                                sx={{
                                                                    fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                                                    color: 'var(--Neutral-neutral-100, #FFF)',
                                                                    fontSize: '16px',
                                                                    fontStyle: 'normal',
                                                                    fontWeight: 600,
                                                                    lineHeight: '110%',
                                                                    textTransform: 'capitalize',
                                                                    width: isMobile ? '100%' : 'auto',
                                                                    '&.Mui-disabled': {
                                                                        color: 'var(--Neutral-neutral-400, #9CA3AF)',
                                                                        backgroundColor: 'var(--Neutral-neutral-200, #E5E7EB)',
                                                                    },
                                                                }}
                                                            >
                                                                Install
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                            )
                                        ) : !isEntitled && solution.productizedIntegrationType === 'Ellucian Core' && solution.salesforceId && status?.erpType ? (
                                            /* Show Select Integration for Ellucian Core solutions with valid salesforceId */
                                            <SelfEntitleButton
                                                solutionId={solution.salesforceId}
                                                fullHierarchy={solution.fullHierarchy}
                                                name={solution.name}
                                                erpType={status.erpType}  // Use actual account's ERP type from status API
                                                remainingCount={calculateSelfEntitlementCounts(status.erpType).remainingCount}
                                                onError={(message) => {
                                                    setSelfEntitleAlertText(message);
                                                    setSelfEntitleAlertOpen(true);
                                                }}
                                                onSuccess={() => {
                                                    // Reload page to show "Request Install" button
                                                    window.location.reload();
                                                }}
                                            />
                                        ) : !isEntitled && ['Ellucian Premium', 'Partner Resell', 'Partner Referral'].includes(solution.productizedIntegrationType ?? '') ? (
                                            /* Show only Request Information for Premium/Resell/Referral solutions when not entitled (non-beta users) */
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                disableElevation
                                                onClick={() => setInfoDialogOpen(true)}
                                                sx={{
                                                    fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                                    color: 'var(--Neutral-neutral-100, #FFF)',
                                                    fontSize: '16px',
                                                    fontStyle: 'normal',
                                                    fontWeight: 600,
                                                    lineHeight: '110%',
                                                    textTransform: 'capitalize',
                                                    width: isMobile ? '100%' : 'auto',
                                                }}
                                            >
                                                Request Information
                                            </Button>
                                        ) : !isEntitled ? (
                                            /* Show Install not available + Request Information for non-Core or invalid salesforceId solutions */
                                            <Box sx={{ display: 'flex', gap: '8px', flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                                                <Tooltip title="This solution is not available for your organization. You can request more information to learn about availability options.">
                                                    <span style={{
                                                        display: 'block',
                                                        width: isMobile ? '100%' : 'fit-content'
                                                    }}>
                                                        <Button
                                                            color="primary"
                                                            variant="contained"
                                                            disableElevation
                                                            disabled
                                                            sx={{
                                                                fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                                                color: 'var(--Neutral-neutral-100, #FFF)',
                                                                fontSize: '16px',
                                                                fontStyle: 'normal',
                                                                fontWeight: 600,
                                                                lineHeight: '110%',
                                                                textTransform: 'capitalize',
                                                                pointerEvents: 'none',
                                                                width: isMobile ? '100%' : 'auto',
                                                                '&.Mui-disabled': {
                                                                    color: 'var(--Neutral-neutral-400, #9CA3AF)',
                                                                    backgroundColor: 'var(--Neutral-neutral-200, #E5E7EB)',
                                                                },
                                                            }}
                                                        >
                                                            Install not available
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    disableElevation
                                                    onClick={() => setInfoDialogOpen(true)}
                                                    sx={{
                                                        fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                                        color: 'var(--Neutral-neutral-100, #FFF)',
                                                        fontSize: '16px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 600,
                                                        lineHeight: '110%',
                                                        textTransform: 'capitalize',
                                                        width: isMobile ? '100%' : 'auto',
                                                    }}
                                                >
                                                    Request Information
                                                </Button>
                                            </Box>
                                        ) : null)}

                                    <Dialog
                                        open={dialogOpen}
                                        onClose={(event: any, reason: string) => {
                                            // Only allow closing via the title bar close button
                                            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                                                return;
                                            }
                                            setDialogOpen(false);
                                        }}
                                        disableEscapeKeyDown // Prevent closing with Escape key
                                        showTitleBarClose={true} // Enable the built-in close button
                                        PaperProps={{
                                            sx: {
                                                m: 0,
                                                width: '100%',
                                                maxWidth: '872px',
                                                borderRadius: '16px',
                                                boxShadow: '0px 8px 32px 0px rgba(0,0,0,0.16)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                background: '#fff !important',
                                                position: 'relative',
                                                zIndex: 1400,
                                                alignSelf: 'center',
                                            }
                                        }}
                                        BackdropProps={{
                                            sx: {
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                width: '100vw',
                                                height: '100vh',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                background: 'rgba(0, 0, 0, 0.30) !important',
                                                zIndex: 1300,
                                            }
                                        }}
                                    >
                                        <DialogTitle
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '24px 24px 0 24px',
                                                alignSelf: 'stretch',
                                            }}
                                        >
                                            <Typography
                                                variant="h2"
                                                sx={{
                                                    color: 'var(--Neutral-neutral-600, #151618)',
                                                    fontWeight: 700,
                                                    fontSize: '24px',
                                                    lineHeight: '36px',
                                                    letterSpacing: '0.72px',
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    padding: 0,
                                                    margin: 0,
                                                }}
                                            >
                                                {`Install ${title}`}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: 'var(--Neutral-neutral-600, #151618)',
                                                    fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                                                    fontSize: '16px',
                                                    fontWeight: 400,
                                                    lineHeight: '24px',
                                                    fontStyle: 'normal',
                                                    padding: 0,
                                                    margin: 0,
                                                    marginTop: '24px',
                                                    marginBottom: '-4px',
                                                }}
                                            >
                                                Choose the environment to install into
                                            </Typography>
                                            {/* If you want a close button, add it here aligned right */}
                                        </DialogTitle>
                                        <DialogContent
                                            sx={{
                                                padding: '24px 32px 32px 32px',
                                            }}
                                        >
                                            {/* ...existing code... */}
                                            <Table sx={{ mt: '0 !important', pt: '0 !important' }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: 60, padding: '8px 8px 8px 16px', textAlign: 'center' }}></TableCell>
                                                        <TableCell sx={{ padding: '8px 16px', fontWeight: 700, fontSize: '16px' }}>Tenant Name</TableCell>
                                                        <TableCell sx={{ padding: '8px 16px', fontWeight: 700, fontSize: '16px' }}>Tenant ID</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {tenants.map((tenant, idx, arr) => (
                                                        <TableRow
                                                            key={tenant.id}
                                                            sx={{
                                                                height: 45,
                                                                borderBottom: '1px solid #E0E0E0'
                                                            }}
                                                        >
                                                            <TableCell
                                                                sx={{
                                                                    width: 60,
                                                                    padding: '8px 8px 8px 16px',
                                                                    border: 0,
                                                                    verticalAlign: 'middle',
                                                                    textAlign: 'center',
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        height: '100%',
                                                                    }}
                                                                >
                                                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0, width: '100%', height: '100%' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="tenant"
                                                                            value={tenant.id}
                                                                            checked={selectedTenants === tenant.id}
                                                                            onChange={(e) => {
                                                                                if (e.target.checked) {
                                                                                    setSelectedTenants(tenant.id);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                margin: 0,
                                                                                width: 16,
                                                                                height: 16,
                                                                                accentColor: selectedTenants === tenant.id ? '#026BC8' : 'var(--Neutral-neutral-500, #6B6F76)'
                                                                            }}
                                                                        />
                                                                    </label>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    padding: '8px 16px',
                                                                    color: 'var(--Neutral-neutral-600, #151618)',
                                                                    fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                                                                    fontSize: '14px',
                                                                    fontStyle: 'normal',
                                                                    fontWeight: 400,
                                                                    lineHeight: '21px',
                                                                    verticalAlign: 'middle',
                                                                }}
                                                            >
                                                                {tenant.name}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    padding: '8px 16px',
                                                                    color: 'var(--Neutral-neutral-600, #151618)',
                                                                    fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                                                                    fontSize: '14px',
                                                                    fontStyle: 'normal',
                                                                    fontWeight: 400,
                                                                    lineHeight: '21px',
                                                                    verticalAlign: 'middle',
                                                                }}
                                                            >
                                                                {tenant.id}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </DialogContent>
                                        <DialogActions sx={{ gap: 0 }}>
                                            <Button
                                                color="secondary"
                                                variant="contained"
                                                onClick={() => setDialogOpen(false)}
                                            >
                                                CANCEL
                                            </Button>
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                disabled={selectedTenants === ''}  // Changed from .length === 0 to === ''
                                                onClick={() => {
                                                    setDialogOpen(false);
                                                }}
                                            >
                                                REVIEW
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                </Box>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', width: isMobile ? '100%' : 'auto', gap: '16px' }}>
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        disableElevation
                                        onClick={() => {
                                            // Track login attempt from solution install
                                            trackUserLogin('okta_sso', false, 'marketplace_user');
                                            navigate('/details/sign-in');
                                        }}
                                        sx={{
                                            color: 'var(--Interactive-Blue-cta-blue-600, #026BC8)',
                                            fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                            fontSize: '16px',
                                            fontStyle: 'normal',
                                            fontWeight: 600,
                                            lineHeight: '110%', // 17.6px
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        Sign In To Install
                                    </Button>
                                </div>
                            )}
                        </Box>

                        {/* Service Types Badges - Show only when logged in */}
                        {isLoggedIn && (
                            <Box sx={{
                                display: 'flex',
                                gap: '8px',
                                flexWrap: 'wrap',
                                alignItems: 'center'
                            }}>
                                {/* Show Professional Services badge */}
                                {solution?.additionalPurchaseTypes && solution.additionalPurchaseTypes.includes('Professional Services Recommended') && (
                                    <div style={{ display: 'inline-block' }}>
                                        <StatusLabel
                                            id="solutionHeader_ProfessionalServices"
                                            text="PROFESSIONAL SERVICES REQUIRED"
                                            type="default"
                                            icon={<Employment />}
                                        />
                                    </div>
                                )}
                                {/* Show Partner Services badge */}
                                {solution?.additionalPurchaseTypes && solution.additionalPurchaseTypes.includes('Partner Services Recommended') && (
                                    <div style={{ display: 'inline-block' }}>
                                        <StatusLabel
                                            id="solutionHeader_PartnerServices"
                                            text="PARTNER SERVICES REQUIRED"
                                            type="default"
                                            icon={<HandsHelping />}
                                        />
                                    </div>
                                )}
                            </Box>
                        )}

                        {/* Rating + Installs (commented out as per request, spacing preserved) */}
                        <Box sx={{ mt: 1, height: 24 }} />
                        {/* Headline */}
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'var(--Neutral-neutral-600, #151618)',
                                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '24px',
                                alignSelf: 'stretch',
                            }}

                            dangerouslySetInnerHTML={sanitizeContent(headline || description)}
                        >
                        </Typography>
                    </Box>
                </Box>
                {/* Install Review Dialog removed as per request */}
                <InstallStatusDrawer
                    open={installStatusOpen}
                    onClose={() => setInstallStatusOpen(false)}
                    solutionName={title}
                    tenantId=""
                    integrationId={solution?.storefrontIntegrationId || solution?.id || ''}
                    erpType={status?.erpType || ''}
                />
                <InstallConfirmationDialog
                    open={confirmationDialogOpen}
                    onClose={() => setConfirmationDialogOpen(false)}
                    onDone={() => {
                        setConfirmationDialogOpen(false);
                        setInstalling(true);
                    }}
                />
                <RequestInstallDialog
                    open={requestInstallDialogOpen}
                    onClose={() => setRequestInstallDialogOpen(false)}
                    onError={(message) => {
                        setSelfEntitleAlertText(message);
                        setSelfEntitleAlertOpen(true);
                    }}
                    onConfirm={() => {
                        setRequestInstallDialogOpen(false);
                        setConfirmationDialogOpen(true);
                    }}
                    solution={solution}
                    erpType={status?.erpType ?? ""}
                />
                {/* Single RequestInfoDialog for all solution types.
            For Premium/Partner Resell, onSubmitOverride routes to Marketo.
            For all others, defaults to the existing /api/leads POST. */}
                {/* Off-screen form for Marketo SDK to inject hidden fields (lpId, subId, etc.).
            Position fixed off-screen (not display:none) so the SDK renders into it. */}
                {isMarketoSolution && (
                    <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: 1, height: 1, overflow: 'hidden', pointerEvents: 'none' }}>
                        <form id="mktoForm_2266" />
                    </div>
                )}
                <RequestInfoDialog
                    open={infoDialogOpen}
                    onClose={() => setInfoDialogOpen(false)}
                    form={infoForm}
                    setForm={setInfoForm}
                    formValid={!!infoFormValid}
                    productName={props.title}
                    solution={solution}
                    onSubmitOverride={isMarketoSolution ? handleMarketoSubmit : undefined}
                />
            </Box>
        </>
    );
};

export default SolutionHeader;